/* eslint-disable camelcase */
/* eslint-disable react/jsx-pascal-case */
/* eslint-disable object-shorthand */
import { useEffect, useState } from 'react';
import * as Analytics from 'expo-firebase-analytics';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Linking, StyleSheet, useWindowDimensions } from 'react-native';
import * as Progress from 'react-native-progress';
import { TextWrap as Text, View, TextInput, HelperText, SplitBox, styles as sc } from '../../components/Themed';
import { RootStackScreenProps } from '../../navigation/navigationTypes';

import TextButton from '../../components/elements/TextButton';
import apiConst from '../../constants/Api';
import { ApiSuccessResponse } from '../../types/AppTypes';
import SnapCalorieSitePage from '../../components/SnapCalorieSitePage';
import Colors from '../../constants/Colors';

/*
This page *should* be hit by a user going to a static Stirpe checkout page, then being redirected to:
api.snapcalorie.com/checkout/success?session_id={CHECKOUT_SESSION_ID}&redirect_url=app.snapcalorie.com/checkut-success
this should store their paid status on the backend and trigger a redirect here with the following url params:
- email
- name
- customer_id
- existingUser
- userHasIdentity
Existing user means they are a user in the db. Has identity means they also have a password
Test with the following url in browser when running locally.
// eslint-disable-next-line max-len
http://localhost:19006/create-account?email=wnorris%40snapcalorie.com&name=Wade&customer_id=cus_LhvmEuBVYyAa1r&existingUser=true&userHasIdentity=true
*/

type NewAccountFormProps = {
  email: string;
  name: string;
  onComplete: () => void;
};
export function NewAccountForm(props: NewAccountFormProps): JSX.Element {
  // From checkout flow
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [email, setEmail] = useState(props.email.trim());
  const [name, setName] = useState(props.name);

  // Page components
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');

  const [showFormErrors, setShowFormErrors] = useState(false);
  const [formErrorMessage, setFormErrorMessage] = useState('');

  const onChangePassword = (newText: string) => setPassword(newText);
  const onChangePasswordConfirm = (newText: string) => setPasswordConfirm(newText);
  const onChangeName = (newText: string) => setName(newText);

  const hasPasswordErrors = () => {
    return password.length < 8;
  };

  const hasPasswordConfirmErrors = () => {
    return password !== passwordConfirm;
  };

  const hasNameErrors = () => {
    return !name || name.length < 1;
  };

  const handleSubmit = () => {
    if (hasNameErrors() || hasPasswordErrors() || hasPasswordConfirmErrors()) {
      setShowFormErrors(true);
    } else {
      const backendUrl = `${apiConst.backendUrl}/auth/cognito/register`;
      const registerBody = {
        name: name,
        email: email,
        password: password,
      };
      fetch(backendUrl, {
        method: 'POST',
        body: new URLSearchParams(registerBody),
      })
        .then((resp) => {
          resp.json().then((jsonResp) => {
            if (resp.status === 201) {
              props.onComplete();
            } else {
              console.error(`An error occurred during registration: ${(jsonResp as ApiSuccessResponse).message}`);
              setFormErrorMessage((jsonResp as ApiSuccessResponse).message!);
            }
          });
        })
        .catch((err) => {
          setFormErrorMessage(err);
        });
    }
  };

  return (
    <>
      <Text style={[sc.focusText, { fontSize: 20, textAlign: 'center' }]}>
        Create your account to sign-in to SnapCalorie.
      </Text>
      <Text style={[sc.focusText, { fontSize: 20, fontWeight: '700', color: 'black' }]}>
        <u>{email && email.length > 0 ? email : 'Email'}</u>
      </Text>
      <SplitBox
        flex={[1]}
        mobileView={false}
        style={{
          alignSelf: 'center',
        }}
        key="SplitBox"
      >
        <View
          style={{
            maxWidth: 300,
          }}
          key="ContainerView"
        >
          <TextInput placeholder="Name" value={name} onChangeText={onChangeName} />
          <HelperText style={showFormErrors ? sc.invalidText : sc.hintText} visible={hasNameErrors()}>
            Please enter your name.
          </HelperText>
          <View>
            <TextInput placeholder="Password" value={password} onChangeText={onChangePassword} secureTextEntry />
            {/* <PasswordStrengthBar password={password} minLength={8} /> */}
            <HelperText style={showFormErrors ? sc.invalidText : sc.hintText} visible={hasPasswordErrors()}>
              Password must be at least 8 characters.
            </HelperText>
          </View>
          <View>
            <TextInput
              placeholder="Confirm Password"
              value={passwordConfirm}
              onChangeText={onChangePasswordConfirm}
              secureTextEntry
            />
            <HelperText style={showFormErrors ? sc.invalidText : sc.hintText} visible={hasPasswordConfirmErrors()}>
              Password confirmation does not match.
            </HelperText>
          </View>
          <TextButton
            onPress={() => {
              handleSubmit();
            }}
            text="Create My Account"
            textStyle={sc.buttonText}
            style={[sc.buttonWrapper, { backgroundColor: '#2364AA' }]}
          />
          <HelperText style={sc.invalidText} visible={formErrorMessage.length > 0}>
            {formErrorMessage}
          </HelperText>
        </View>
      </SplitBox>
    </>
  );
}

type VerifyAccountFormProps = {
  email: string;
  maxWidth: number;
  onComplete: () => void;
};
export function VerifyAccountForm(props: VerifyAccountFormProps): JSX.Element {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [email, setEmail] = useState(props.email.trim());
  const [verificationCode, setVerificationCode] = useState('');
  const [showFormErrors, setShowFormErrors] = useState(false);
  const [formErrorMessage, setFormErrorMessage] = useState('');

  const onChangeVerificationCode = (newText: string) => setVerificationCode(newText);
  const hasVerificationErrors = () => {
    return verificationCode.length < 6;
  };

  const handleResendCode = () => {
    const backendUrl = `${apiConst.backendUrl}/auth/cognito/resend-confirmation`;
    const resendBody = {
      email: email,
    };
    fetch(backendUrl, {
      method: 'POST',
      body: new URLSearchParams(resendBody),
    })
      .then((resp) => {
        resp.json().then((jsonResp) => {
          if (resp.status === 200) {
            console.log(`New confirmation requested`);
          } else {
            // eslint-disable-next-line max-len
            console.error(
              `An error occurred requesting a new verification code: ${(jsonResp as ApiSuccessResponse).message}`
            );
            setFormErrorMessage((jsonResp as ApiSuccessResponse).message!);
          }
        });
      })
      .catch((err) => {
        setFormErrorMessage(err);
      });
  };

  const handleSubmit = () => {
    if (hasVerificationErrors()) {
      setShowFormErrors(true);
    } else {
      const backendUrl = `${apiConst.backendUrl}/auth/cognito/confirm`;
      const verifyBody = {
        email: email,
        code: verificationCode,
      };
      fetch(backendUrl, {
        method: 'POST',
        body: new URLSearchParams(verifyBody),
      })
        .then((resp) => {
          resp.json().then((jsonResp) => {
            if (resp.status === 200) {
              console.log(`Email verified`);
              props.onComplete();
            } else {
              console.error(`An error occurred during email verification: ${(jsonResp as ApiSuccessResponse).message}`);
              setFormErrorMessage((jsonResp as ApiSuccessResponse).message!);
            }
          });
        })
        .catch((err) => {
          setFormErrorMessage(err);
        });
    }
  };

  return (
    <>
      <Text style={[sc.focusText, { fontSize: 20, textAlign: 'center', maxWidth: props.maxWidth }]}>
        Enter the 6 digit code that was sent to <u>{email && email.length > 0 ? email : 'your email.'}</u>
      </Text>
      <View
        style={{
          maxWidth: props.maxWidth,
          alignSelf: 'center',
          marginVertical: 30,
        }}
        key="ContainerView"
      >
        <View>
          <TextInput
            placeholder="Verification Code"
            value={verificationCode}
            onChangeText={onChangeVerificationCode}
            maxLength={6}
            style={{ width: 300, alignSelf: 'center' }}
          />
          <HelperText style={showFormErrors ? sc.invalidText : sc.hintText} visible={hasVerificationErrors()}>
            Verification code is 6 digits.
          </HelperText>
        </View>
        <TextButton
          onPress={() => {
            handleSubmit();
          }}
          text="Verify Email"
          textStyle={sc.buttonText}
          style={[sc.buttonWrapper, { backgroundColor: '#2364AA' }]}
          disabled={hasVerificationErrors()}
        />
        <HelperText style={sc.invalidText} visible={formErrorMessage.length > 0}>
          {formErrorMessage}
        </HelperText>
        <Text style={[sc.focusText, { fontSize: 16, textAlign: 'center' }]}>
          If you did not receive a verification code in your email click the link below to request a new one.
        </Text>
        <Text
          onPress={() => {
            handleResendCode();
          }}
          style={[sc.link, { marginTop: 10 }]}
        >
          Resend Verification Code
        </Text>
      </View>
    </>
  );
}

type RegistrationCompleteProps = {
  maxWidth: number;
};

export function RegistrationComplete(props: RegistrationCompleteProps): JSX.Element {
  return (
    <>
      <Text style={[sc.h3, { fontSize: 24 }]}>Upgrade Complete</Text>
      <Text style={[sc.focusText, { fontSize: 20, textAlign: 'center', maxWidth: props.maxWidth, marginTop: 15 }]}>
        Your account has now been setup and upgraded to premium. Continue to download the app.
      </Text>
      <TextButton
        onPress={() => {
          // This has a weird name, but it's the download page with the ios/Android imagery.
          // We use this static page for reporting conversion to Google/FB.
          Linking.openURL('https://www.snapcalorie.com/purchase/');
        }}
        text="Continue"
        textStyle={sc.buttonText}
        style={[sc.buttonWrapper, { backgroundColor: '#2364AA' }]}
      />
      <View
        style={{
          maxWidth: props.maxWidth,
          alignSelf: 'center',
        }}
        key="ContainerView"
      >
        <Text style={[sc.focusText, { fontSize: 16, textAlign: 'center' }]}>
          If you encounter problems installing or logging in to the SnapCalorie App on your device, contact us at:
        </Text>
        <Text style={[sc.link, { marginTop: 0 }]}>
          <a href="mailto:help@snapcalorie.com">help@snapcalorie.com</a>
        </Text>
      </View>
    </>
  );
}

enum AccountCreationStep {
  LOADING,
  REGISTER,
  VERIFY,
  COMPLETE,
  EXPIRED,
}

export default function CreateAccount({ navigation, route }: RootStackScreenProps<'CreateAccount'>) {
  // From checkout flow
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  const [step, setStep] = useState(AccountCreationStep.LOADING);
  const windowDimensions = useWindowDimensions();

  useEffect(() => {
    if (step === AccountCreationStep.COMPLETE) {
      const deleteLocalStorage = async () => {
        await AsyncStorage.multiRemove([
          '@snapcalorie_checkout_email_address',
          '@snapcalorie_checkout_name',
          '@snapcalorie_checkout_customer_id',
          '@snapcalorie_checkout_existing_user_tf',
          '@snapcalorie_checkout_user_has_identity',
          '@snapcalorie_survey_responses',
        ]);
      };
      deleteLocalStorage();
    }
  }, [navigation, step]);

  // Get or set items in Async storage
  useEffect(() => {
    const initializeParams = async () => {
      // we can come into this page by 1) redirect from Stripe with relevant params, in which case we
      // should save them or 2) a user navigates to this page directly, in which case we should check
      // if the relevant params are in local storage.
      console.log('Initializing');
      if (route.params && route.params.customer_id && route.params.email) {
        console.log('Saving params to localStorage');
        try {
          await AsyncStorage.setItem('@snapcalorie_checkout_customer_id', route.params.customer_id ?? '');
          await AsyncStorage.setItem('@snapcalorie_checkout_email_address', route.params.email ?? '');
          await AsyncStorage.setItem('@snapcalorie_checkout_name', route.params.name ?? '');
          await AsyncStorage.setItem('@snapcalorie_checkout_completed_at', Date.now().toString());

          if (route.params.userHasIdentity === 'true') {
            await AsyncStorage.setItem('@snapcalorie_checkout_user_has_identity', 'true');
          }
        } catch (e) {
          console.error(e);
        }
      }
      try {
        const checkoutEmail =
          route.params && route.params.email !== undefined
            ? route.params.email
            : await AsyncStorage.getItem('@snapcalorie_checkout_email_address');
        const checkoutName =
          route.params && route.params.name !== undefined
            ? route.params.name
            : await AsyncStorage.getItem('@snapcalorie_checkout_name');

        const skipRegistrarion =
          (route.params && route.params.userHasIdentity === 'true') ||
          (await AsyncStorage.getItem('@snapcalorie_checkout_user_has_identity')) === 'true';

        if (!checkoutEmail) {
          throw new Error('Email not found in local storage');
        }

        setEmail(checkoutEmail.trim() ?? '');
        setName(checkoutName ?? '');

        if (skipRegistrarion) {
          setStep(AccountCreationStep.COMPLETE);
        } else {
          setStep(AccountCreationStep.REGISTER);
        }
      } catch (e) {
        console.error(e);
        setStep(AccountCreationStep.EXPIRED);
      }
    };
    initializeParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (step === AccountCreationStep.LOADING) {
      Analytics.logEvent('conversion-paid');
    } else if (step === AccountCreationStep.COMPLETE) {
      Analytics.logEvent('conversion-registrationcomplete');
    }
    Analytics.logEvent('conversion-create-account-pagechange');
  }, [step]);
  Analytics.logEvent('conversion-create-account-pageload');

  return (
    <SnapCalorieSitePage
      headerLinks={{
        Home: () => {
          navigation.navigate('Home');
        },
      }}
    >
      {step !== AccountCreationStep.EXPIRED && (
        <View style={{ alignSelf: 'center', marginBottom: 40 }}>
          <Text style={[sc.h4, { marginBottom: 10 }]}>
            Step {step} of {3}
          </Text>
          <Progress.Bar
            color={Colors.snapCalorie.orange}
            borderColor="#888"
            borderRadius={5}
            borderWidth={2}
            progress={step / 3}
            height={12}
            width={345}
          />
        </View>
      )}
      {step === AccountCreationStep.LOADING && (
        <Text style={[sc.blockParagraph, { textAlign: 'center' }]}>Loading...</Text>
      )}
      {step === AccountCreationStep.REGISTER && (
        <NewAccountForm email={email} name={name} onComplete={() => setStep(AccountCreationStep.VERIFY)} />
      )}
      {step === AccountCreationStep.VERIFY && (
        <VerifyAccountForm
          email={email}
          onComplete={() => setStep(AccountCreationStep.COMPLETE)}
          maxWidth={Math.min(windowDimensions.width, 350)}
        />
      )}
      {step === AccountCreationStep.COMPLETE && (
        <RegistrationComplete maxWidth={Math.min(windowDimensions.width, 350)} />
      )}
      {step === AccountCreationStep.EXPIRED && (
        <>
          <Text style={[sc.h3, { fontSize: 24 }]}>Lost your way?</Text>
          <Text style={[sc.h3, { fontSize: 16 }]}>
            <a href="https://www.snapcalorie.com/download" target="_self">
              Download the app here.
            </a>
          </Text>
          <View style={{ height: 40 }} />
          <Text style={[sc.blockParagraph, { fontSize: 16 }]}>
            If you encountered an error creating your account please contact {}
            <a href="mailto:help@snapcalorie.com">help@snapcalorie.com</a> for support.
          </Text>
        </>
      )}
    </SnapCalorieSitePage>
  );
}

export const styles = StyleSheet.create({
  textInput: {
    height: 40,
    marginTop: 12,
    borderWidth: 1,
    padding: 10,
    borderBottomEndRadius: 10,
    borderTopEndRadius: 10,
    borderBottomStartRadius: 10,
    borderTopStartRadius: 10,
    overflow: 'hidden',
    textAlign: 'center',
    backgroundColor: 'white',
  },
});
